<template>
  <div
    class="container py-10"
    :class="{ 'mobile-container': isMobileApp, 'safe-area': isIosApp }">
    <router-view
      v-if="$store.state.tenant"
      :key="$route.fullPath"></router-view>
  </div>

  <BottomBar v-if="isMobileApp" />

  <div
    v-if="$store.state.order.loading"
    class="uk-overlay bg-white fixed top-0 left-0 right-0 bottom-0 z-50 uk-flex uk-flex-center uk-flex-middle uk-flex-column"
    style="height: 100%">
    <div uk-spinner="ratio: 2" class="mb-4 !text-gray-800"></div>
    <h4 class="text-center">{{ $t("MODAL.loadingMessage") }}</h4>
  </div>
</template>

<script>
import { registerPlugin } from "@capacitor/core";
import AccountApi from "@/services/account.api";
import { isPublicRoute } from "@/router";
import {
  bracketHandler,
  decodeToken,
  encodeToken,
  isMobileApp,
  isIosApp,
  TRAINPLANET_SWISH_CALLBACK_CREATED_AT_QUERY_KEY,
  TRAINPLANET_SWISH_CALLBACK_MAX_AGE_MS,
  TRAINPLANET_SWISH_CALLBACK_URL,
} from "@/utils/helper.js";
import createHost from "@midorina/cross-domain-storage/host.js";
import * as CryptoJS from "crypto-js";
import queryString from "query-string";
import TrainplanetApi from "./services/trainplanet.api";
import { mapGetters } from "vuex";
import BottomBar from "./components/BottomBar.vue";
import PullToRefresh from "pulltorefreshjs";

const CapacitorApp = registerPlugin("App");

export default {
  name: "App",
  components: { BottomBar },
  data: () => ({
    whitelist: [],
    frame: null,
    user: null,
    isMobileApp: isMobileApp(),
    isIosApp: isIosApp(),
    nativeAppUrlListenerHandle: null,
  }),
  computed: {
    ...mapGetters({
      tenant: "tenant",
    }),
    AccountApiModel() {
      return new AccountApi(this.$store.state.tenant.id);
    },
    TrainPlanetModel() {
      if (this.$store.state.tenant.id) {
        return new TrainplanetApi(this.$store.state.tenant.id);
      }
    },
  },
  watch: {
    async $route(to, from) {
      const removeQueryParam = (queryParam) => {
        const url = new URL(window.location.href);
        url.searchParams.delete(queryParam);
        window.history.replaceState({}, document.title, url.toString());
      };

      if (to.query.accesstoken && to.query.refreshtoken) {
        removeQueryParam("accesstoken");
        removeQueryParam("refreshtoken");

        const url = new URL(window.location.href);
        if (
          url !== bracketHandler(process.env.VUE_APP_TRAINPLANET_PAY_URL) &&
          to.query.controlled
        ) {
          removeQueryParam("controlled");
        }
      }

      if (from.name == "OrderDetails") {
        var tenant = await TrainplanetApi.getTenant();
        document.documentElement.setAttribute(
          "theme",
          tenant.name.toLowerCase()
        );
        document.title = tenant.name;
        const favicon = document.getElementById("favicon");
        favicon.href = tenant.theme.favIconUrl;
        window.location.reload();
      }
      if (localStorage.getItem("refresh_token")) {
        this.AccountApiModel.refreshToken({
          refreshToken: localStorage.getItem("refresh_token"),
        }).finally(() => {
          this.loading = false;
        });
      }
    },
    "$i18n.locale": {
      handler() {
        this.refresherInit();
      },
      immediate: true,
    },
  },

  created: function () {
    var payUrl = process.env.VUE_APP_TRAINPLANET_PAY_URL;

    var url = new URL(window.location.href);

    const query = queryString.parse(url.search);
    if (
      localStorage.getItem("refresh_token") &&
      !query.logout &&
      !query.logtick
    ) {
      this.AccountApiModel.refreshToken({
        refreshToken: localStorage.getItem("refresh_token"),
      }).finally(() => {
        this.loading = false;
      });
    } else if (
      // No session to restore, so a page that needs one has to send the user to
      // login. This used to read the route name out of sessionStorage, which only
      // Header.vue ever wrote and so described whatever page was visited last.
      !isPublicRoute() &&
      url.origin != bracketHandler(payUrl) &&
      !window.location.href.includes("booking_number") &&
      !window.location.href.includes("from")
    ) {
      setTimeout(() => {
        this.$router.push("");
      }, 1500);
    }

    if (this.isIosApp) {
      document.body.classList.add("safe-body");
    }

    var storageHost = createHost([
      // MAIN PAGE
      {
        origin: "https://norrtag.se",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://staging.norrtag.se",
        allowedMethods: ["get", "set", "remove"],
      },
      //DEV CUSTOMER

      {
        origin: "https://dev.customer.interrail.no",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://dev.customer.interrail.se",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://dev.customer.europarunt.se",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://dev.customer.europarunt.no",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://dev.minasidor.tagbokningen.se",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://dev.minasidor.oresundstag.trainplanet.com",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://dev.minasidor.resrobot.trainplanet.com",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://dev.customer.trainplanet.com",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://dev.minasidor.norrtag.se",
        allowedMethods: ["get", "set", "remove"],
      },

      //DEV TİCKETS

      {
        origin: "https://dev.boka.tagbokningen.se",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://dev.boka.oresundstag.trainplanet.com",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://dev.kopbiljett.resrobot.trainplanet.com",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://dev.shop.interrail.se",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://dev.shop.interrail.no",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://dev.shop.europarunt.se",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://dev.shop.europarundt.no",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://dev.shop.europarundt.dk",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://dev.tickets.trainplanet.com",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://dev.boka.norrtag.se",
        allowedMethods: ["get", "set", "remove"],
      },

      //CUSTOMER PAGES
      {
        origin: "https://minasidor.norrtag.se",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://customer.interrail.no",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://customer.interrail.se",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://customer.europarunt.se",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://customer.europarunt.no",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://minasidor.tagbokningen.se",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://minasidor.oresundstag.trainplanet.com",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://minasidor.resrobot.trainplanet.com",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://customer.trainplanet.com",
        allowedMethods: ["get", "set", "remove"],
      },

      // shoping URL
      {
        origin: "https://boka.tagbokningen.se",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://boka.oresundstag.se",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://kopbiljett.resrobot.trainplanet.com",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://shop.interrail.se",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://shop.interrail.no",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://shop.europarunt.se",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://shop.europarundt.no",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://shop.europarundt.dk",
        allowedMethods: ["get", "set", "remove"],
      },

      {
        origin: "https://tickets.trainplanet.com",
        allowedMethods: ["get", "set", "remove"],
      },
      {
        origin: "https://boka.norrtag.se",
        allowedMethods: ["get", "set", "remove"],
      },
    ]);
  },

  async beforeCreate() {
    var payUrl = process.env.VUE_APP_TRAINPLANET_PAY_URL;
    var url = new URL(window.location.href);
    var redirect = "";
    const isPay = url.origin === bracketHandler(payUrl);

    if (
      isPay ||
      url.origin.includes("minasidor") ||
      url.origin.includes("customer")
    ) {
      var to = {
        query: queryString.parse(url.search),
      };
      if (to.query.came) {
        try {
          redirect = decodeURIComponent(to.query.came);
        } catch (e) {
          redirect = to.query.came;
        }

        if (to.query.offerMode) {
          redirect = redirect + "&offerMode=" + to.query.offerMode;
        }
      }

      if (window.location.href.includes("logout")) {
        const authToken = localStorage.getItem("auth_token");
        if (authToken) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("refresh_token");

          try {
            await fetch(
              `${process.env.VUE_APP_TRAINPLANET_API_URL_BASE}/account/jwt/logout`,
              {
                method: "POST",
                headers: {
                  AuthorizationJwt: "Bearer " + authToken,
                },
              }
            );
          } catch (error) {
            console.error("Logout request failed:", error);
          }
        }
        window.location.href = redirect;
      } else if (to.query.logtick == "true") {
        const authToken = localStorage.getItem("auth_token");
        if (authToken) {
          localStorage.clear();

          try {
            await fetch(
              `${process.env.VUE_APP_TRAINPLANET_API_URL_BASE}/account/jwt/logout`,
              {
                method: "POST",
                headers: {
                  AuthorizationJwt: "Bearer " + authToken,
                },
              }
            );
          } catch (error) {
            console.error("Logout request failed:", error);
          }
        }
        window.location.href =
          bracketHandler(payUrl) +
          "/sv?logout=true&came=" +
          encodeURIComponent(redirect);
      } else {
        if (to.query.accesstoken && to.query.refreshtoken) {
          if (to.query.from) {
            const baseUrl = new URL(to.query.from);
            baseUrl.searchParams.set("accesstoken", to.query.accesstoken);
            baseUrl.searchParams.set("refreshtoken", to.query.refreshtoken);
            window.location.href = baseUrl.toString();
          } else {
            const deAccess = CryptoJS.AES.decrypt(
              decodeToken(to.query.accesstoken).substring(6),
              decodeToken(to.query.accesstoken).substring(0, 6)
            ).toString(CryptoJS.enc.Utf8);
            const deRefresh = CryptoJS.AES.decrypt(
              decodeToken(to.query.refreshtoken).substring(6),
              decodeToken(to.query.refreshtoken).substring(0, 6)
            ).toString(CryptoJS.enc.Utf8);

            localStorage.setItem("auth_token", JSON.parse(deAccess).myAuth);
            localStorage.setItem(
              "refresh_token",
              JSON.parse(deRefresh).myRefresh
            );
          }
        }
        if (
          !window.location.href.includes("forControl") &&
          !isPay &&
          !window.location.href.includes("controlled=true") &&
          !window.location.href.includes("logout") &&
          !window.location.pathname.includes("order-details") &&
          !window.location.pathname.includes("cancel-order") &&
          !window.location.pathname.includes("register")
        ) {
          window.location.href =
            bracketHandler(payUrl) +
            "/?forControl=true&came=" +
            window.location.href;
        }

        if (
          (to.query.controlled === "true" &&
            Object.keys(to.query).length === 1) ||
          (to.query.from &&
            to.query.controlled &&
            !to.query.accesstoken &&
            !to.query.refreshtoken)
        ) {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("refresh_token");

          const updatedQuery = { ...to.query };
          delete updatedQuery.controlled;

          const encodedQuery = Object.keys(updatedQuery).reduce((acc, key) => {
            acc[key] = encodeURIComponent(updatedQuery[key]);
            return acc;
          }, {});

          this.$router.replace({ query: encodedQuery });
        }

        if (
          window.location.href.includes(bracketHandler(payUrl)) &&
          to.query.accesstoken
        ) {
          var deAccess = CryptoJS.AES.decrypt(
            decodeToken(to.query.accesstoken).substring(6),
            decodeToken(to.query.accesstoken).substring(0, 6)
          ).toString(CryptoJS.enc.Utf8);
          var deRefresh = CryptoJS.AES.decrypt(
            decodeToken(to.query.refreshtoken).substring(6),
            decodeToken(to.query.refreshtoken).substring(0, 6)
          ).toString(CryptoJS.enc.Utf8);

          localStorage.setItem("auth_token", JSON.parse(deAccess).myAuth);
          localStorage.setItem(
            "refresh_token",
            JSON.parse(deRefresh).myRefresh
          );
          if (redirect.includes("controlled=true")) {
            window.location.href =
              redirect +
              "&accesstoken=" +
              to.query.accesstoken +
              "&refreshtoken=" +
              to.query.refreshtoken;
          } else {
            var subUrl = new URL(redirect);
            var subTo = {
              query: queryString.parse(subUrl.search),
            };

            if (Object.keys(subTo.query).length != 0) {
              window.location.href =
                redirect +
                "&accesstoken=" +
                to.query.accesstoken +
                "&refreshtoken=" +
                to.query.refreshtoken;
            } else {
              window.location.href =
                redirect +
                "?accesstoken=" +
                to.query.accesstoken +
                "&refreshtoken=" +
                to.query.refreshtoken;
            }
          }
        }
        if (
          !window.location.href.includes(bracketHandler(payUrl)) &&
          to.query.accesstoken
        ) {
          var deAccess = CryptoJS.AES.decrypt(
            decodeToken(to.query.accesstoken).substring(6),
            decodeToken(to.query.accesstoken).substring(0, 6)
          ).toString(CryptoJS.enc.Utf8);
          var deRefresh = CryptoJS.AES.decrypt(
            decodeToken(to.query.refreshtoken).substring(6),
            decodeToken(to.query.refreshtoken).substring(0, 6)
          ).toString(CryptoJS.enc.Utf8);
          localStorage.setItem("auth_token", JSON.parse(deAccess).myAuth);
          localStorage.setItem(
            "refresh_token",
            JSON.parse(deRefresh).myRefresh
          );
        }
        if (window.location.href.includes("forControl")) {
          if (localStorage.getItem("auth_token")) {
            var keyAccess = makeid(6);
            var keyRefresh = makeid(6);
            var myAuth = localStorage.getItem("auth_token");
            var myRefresh = localStorage.getItem("refresh_token");
            var encrypted = CryptoJS.AES.encrypt(
              JSON.stringify({ myAuth }),
              keyAccess
            );
            var encryptedRefresh = CryptoJS.AES.encrypt(
              JSON.stringify({ myRefresh }),
              keyRefresh
            );
            var cryptedAccessToken =
              keyAccess + encodeToken(encrypted.toString());
            var cryptedRefreshToken =
              keyRefresh + encodeToken(encryptedRefresh.toString());
            var subUrl = new URL(redirect);
            var subTo = {
              query: queryString.parse(subUrl.search),
            };

            if (
              Object.keys(to.query).length > 3 &&
              (window.navigator.userAgent.toLowerCase().includes("iphone") ||
                (window.navigator.userAgent.toLowerCase().includes("safari") &&
                  !window.navigator.userAgent.toLowerCase().includes("chrome")))
            ) {
              var url = "";

              if (Object.keys(subTo.query).length > 0) {
                url =
                  redirect +
                  "&controlled=true" +
                  "&accesstoken=" +
                  cryptedAccessToken +
                  "&refreshtoken=" +
                  cryptedRefreshToken;

                window.location.href = url;
              } else {
                url =
                  redirect +
                  "?controlled=true" +
                  "&accesstoken=" +
                  cryptedAccessToken +
                  "&refreshtoken=" +
                  cryptedRefreshToken;
                window.location.href = url;
              }
            } else {
              if (Object.keys(subTo.query).length > 0) {
                window.location.href =
                  redirect +
                  "&controlled=true" +
                  "&accesstoken=" +
                  cryptedAccessToken +
                  "&refreshtoken=" +
                  cryptedRefreshToken;
              } else {
                window.location.href =
                  redirect +
                  "?controlled=true" +
                  "&accesstoken=" +
                  cryptedAccessToken +
                  "&refreshtoken=" +
                  cryptedRefreshToken;
              }
            }
          } else {
            if (redirect.includes("from")) {
              if (
                redirect.includes("minasidor") ||
                redirect.includes("customer")
              ) {
                var redirectURL = new URL(redirect);
                var encodeStr = redirect.split("from=")[1];
                window.location.href =
                  redirectURL.origin +
                  redirectURL.pathname +
                  "?from=" +
                  encodeURIComponent(new URL(encodeStr)) +
                  "&controlled=true";
              } else {
                window.location.href = redirect + "&controlled=true";
              }
            } else {
              if (Object.keys(to.query).length > 2) {
                window.location.href = redirect + "&controlled=true";
              } else {
                var subUrl = new URL(redirect);
                var subTo = {
                  query: queryString.parse(subUrl.search),
                };
                if (Object.keys(subTo.query).length > 0) {
                  window.location.href = redirect + "&controlled=true";
                } else {
                  window.location.href = redirect + "?controlled=true";
                }
              }
            }
          }
        }
      }
    }
  },
  async mounted() {
    await this.initNativeAppUrlHandling();
  },
  async beforeUnmount() {
    if (this.nativeAppUrlListenerHandle) {
      await this.nativeAppUrlListenerHandle.remove();
      this.nativeAppUrlListenerHandle = null;
    }
  },
  methods: {
    async initNativeAppUrlHandling() {
      if (!this.isMobileApp) {
        return;
      }

      try {
        this.nativeAppUrlListenerHandle = await CapacitorApp.addListener(
          "appUrlOpen",
          ({ url }) => {
            this.handleNativeAppUrlOpen(url, "appUrlOpen");
          }
        );
      } catch (error) {
        console.error("Failed to register native app URL listener:", error);
      }

      try {
        const launchUrl = await CapacitorApp.getLaunchUrl();
        if (launchUrl?.url) {
          this.handleNativeAppUrlOpen(launchUrl.url, "getLaunchUrl");
        }
      } catch (error) {
        console.error("Failed to read native launch URL:", error);
      }
    },
    parseUrl(value, base = undefined) {
      if (!value) {
        return null;
      }

      try {
        return base ? new URL(value, base) : new URL(value);
      } catch (error) {
        console.error("Failed to parse URL:", value, error);
        return null;
      }
    },
    getSwishCallbackRouteLocation(appUrl, source = "native") {
      const callbackUrl = this.parseUrl(appUrl);
      if (!callbackUrl) {
        return null;
      }

      const swishCallbackBaseUrl = this.parseUrl(
        TRAINPLANET_SWISH_CALLBACK_URL
      );
      if (
        !swishCallbackBaseUrl ||
        callbackUrl.protocol !== swishCallbackBaseUrl.protocol ||
        callbackUrl.hostname !== swishCallbackBaseUrl.hostname
      ) {
        return null;
      }

      let path = this.$route?.path || "/";
      let query = { ...(this.$route?.query || {}) };
      let hash = this.$route?.hash || "";

      const returnUrlValue = callbackUrl.searchParams.get("returnUrl");
      const returnUrl = this.parseUrl(returnUrlValue, window.location.origin);
      if (returnUrl) {
        path = returnUrl.pathname || path;
        query = queryString.parse(returnUrl.search);
        hash = returnUrl.hash || hash;
      }

      const routeLocation = {
        hash,
        path,
        query,
      };

      if (this.shouldIgnoreSwishLaunchUrl(callbackUrl, routeLocation, source)) {
        return null;
      }

      query.showStatusModal =
        callbackUrl.searchParams.get("showStatusModal") || "1";

      const swishSessionId = callbackUrl.searchParams.get("swishSessionId");
      if (swishSessionId) {
        query.swishSessionId = swishSessionId;
      } else {
        delete query.swishSessionId;
      }

      return routeLocation;
    },
    shouldIgnoreSwishLaunchUrl(callbackUrl, routeLocation, source) {
      if (source !== "getLaunchUrl") {
        return false;
      }

      const callbackCreatedAt = Number(
        callbackUrl.searchParams.get(
          TRAINPLANET_SWISH_CALLBACK_CREATED_AT_QUERY_KEY
        )
      );
      const hasCallbackCreatedAt =
        Number.isFinite(callbackCreatedAt) && callbackCreatedAt > 0;
      const isCallbackFresh =
        hasCallbackCreatedAt &&
        Date.now() - callbackCreatedAt <= TRAINPLANET_SWISH_CALLBACK_MAX_AGE_MS;
      const swishSessionId = callbackUrl.searchParams.get("swishSessionId");
      const routeOrderId = this.getRouteLocationOrderId(routeLocation);

      if (hasCallbackCreatedAt && !isCallbackFresh) {
        return true;
      }

      if (
        routeOrderId &&
        swishSessionId &&
        this.hasStoredSwishSession(routeOrderId, swishSessionId)
      ) {
        return false;
      }

      return true;
    },
    getRouteLocationOrderId(routeLocation) {
      const resolvedRoute = this.$router.resolve(routeLocation);

      if (
        resolvedRoute?.name !== "pay" &&
        resolvedRoute?.name !== "confirmation"
      ) {
        return null;
      }

      return resolvedRoute?.params?.orderId || null;
    },
    hasStoredSwishSession(orderId, swishSessionId) {
      if (!orderId || !swishSessionId) {
        return false;
      }

      try {
        const raw =
          localStorage.getItem(`swishSession_${orderId}`) ||
          localStorage.getItem(`swishQrSession_${orderId}`);

        if (!raw) {
          return false;
        }

        const session = JSON.parse(raw);

        return (
          session?.orderId === orderId &&
          String(session.sessionId) === String(swishSessionId) &&
          Date.now() < session.expiresAt
        );
      } catch (error) {
        return false;
      }
    },
    async handleNativeAppUrlOpen(url, source = "native") {
      const routeLocation = this.getSwishCallbackRouteLocation(url, source);
      if (!routeLocation) {
        return;
      }

      const currentRoute = this.$router.currentRoute.value;
      const currentQuery = queryString.stringify(currentRoute?.query || {});
      const nextQuery = queryString.stringify(routeLocation.query || {});

      if (
        currentRoute?.path === routeLocation.path &&
        (currentRoute?.hash || "") === (routeLocation.hash || "") &&
        currentQuery === nextQuery
      ) {
        return;
      }

      try {
        await this.$router.replace(routeLocation);
      } catch (error) {
        console.error(
          `Failed to handle native Swish callback URL from ${source}:`,
          error
        );
      }
    },
    refresherInit() {
      if (this.isMobileApp) {
        PullToRefresh.destroyAll();
        PullToRefresh.init({
          mainElement: "body",
          instructionsPullToRefresh: this.$t("PULL_TO_REFRESH"),
          instructionsReleaseToRefresh: this.$t("RELEASE_TO_REFRESH"),
          instructionsRefreshing: this.$t("REFRESHING"),
          onRefresh() {
            window.location.reload();
          },
          shouldPullToRefresh() {
            return (
              !(
                document.body.style.overflow === "hidden" ||
                document.body.style.overflowY === "hidden"
              ) && !window.scrollY
            );
          },
        });
      }
    },
    makeid(length) {
      let result = "";
      const characters =
        "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
      const charactersLength = characters.length;
      let counter = 0;
      while (counter < length) {
        result += characters.charAt(
          Math.floor(Math.random() * charactersLength)
        );
        counter += 1;
      }
      return result;
    },
  },
};

function makeid(length) {
  let result = "";
  const characters =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  const charactersLength = characters.length;
  let counter = 0;
  while (counter < length) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
    counter += 1;
  }
  return result;
}
</script>

<style lang="scss">
.mobile-container {
  padding-bottom: 110px;
}

.ptr--ptr {
  background-color: #65638d;
}

.ptr--text,
.ptr--icon {
  font-size: 14px;
}

.safe-area {
  .modal,
  .checkout-modal,
  .register-modal,
  .passenger-modal {
    top: env(safe-area-inset-top) !important;
    height: calc(100% - env(safe-area-inset-top)) !important;
  }

  #modal-expire-timer {
    top: env(safe-area-inset-top) !important;
  }
}
</style>
